/**
 * Token管理器
 * 负责token存储、验证、自动刷新等功能
 */
import storage from '../storage'
import { refreshToken as refreshTokenAPI } from '../api/auth'

class TokenManager {
  constructor() {
    this.refreshPromise = null // 防止并发刷新
    this.fetchInterceptors = new Map() // 存储被拦截的fetch请求
  }

  /**
   * 检查token是否过期
   * @param {string} token - JWT token
   * @returns {boolean} 是否过期（提前5分钟判定为过期）
   */
  isTokenExpired(token) {
    if (!token) return true

    try {
      // 解析JWT payload
      const parts = token.split('.')
      if (parts.length !== 3) return true

      const payload = JSON.parse(atob(parts[1]))
      const exp = payload.exp

      if (!exp) return true

      // 提前5分钟判定为过期（避免临界情况）
      const now = Math.floor(Date.now() / 1000)
      const bufferTime = 5 * 60 // 5分钟缓冲

      return now >= (exp - bufferTime)
    } catch (error) {
      console.error('Token解析失败:', error)
      return true
    }
  }

  /**
   * 获取token剩余有效时间（秒）
   * @param {string} token - JWT token
   * @returns {number} 剩余秒数，如果已过期返回0
   */
  getTokenRemainingTime(token) {
    if (!token) return 0

    try {
      const parts = token.split('.')
      if (parts.length !== 3) return 0

      const payload = JSON.parse(atob(parts[1]))
      const exp = payload.exp

      if (!exp) return 0

      const now = Math.floor(Date.now() / 1000)
      const remaining = exp - now

      return remaining > 0 ? remaining : 0
    } catch (error) {
      return 0
    }
  }

  /**
   * 获取有效的access_token（自动刷新）
   * @returns {Promise<string>} 有效的access_token
   * @throws {Error} AUTH_EXPIRED - token完全失效需要重新登录
   */
  async getValidAccessToken() {
    const accessToken = storage.get('token')
    const refreshToken = storage.get('refreshToken')

    // 检查access_token是否有效
    if (accessToken && !this.isTokenExpired(accessToken)) {
      return accessToken
    }

    // access_token过期，检查refresh_token
    if (!refreshToken || this.isTokenExpired(refreshToken)) {
      // refresh_token也过期，需要重新登录
      throw new Error('AUTH_EXPIRED')
    }

    // 需要刷新token
    // 防止并发刷新：如果已有刷新进程，等待其完成
    if (this.refreshPromise) {
      try {
        return await this.refreshPromise
      } catch (error) {
        // 如果并发刷新失败，重新抛出错误
        throw error
      }
    }

    // 开始刷新流程
    this.refreshPromise = this.doRefreshToken()

    try {
      const newToken = await this.refreshPromise
      return newToken
    } catch (error) {
      throw error
    } finally {
      // 清理刷新promise
      this.refreshPromise = null
    }
  }

  /**
   * 强制刷新access_token（不管access_token是否过期）
   * @returns {Promise<string>} 新的access_token
   */
  async refreshAccessToken() {
    // 防止并发刷新：如果已有刷新进程，等待其完成
    if (this.refreshPromise) {
      return await this.refreshPromise
    }

    this.refreshPromise = this.doRefreshToken()

    try {
      return await this.refreshPromise
    } finally {
      this.refreshPromise = null
    }
  }

  /**
   * 执行token刷新
   * @private
   * @returns {Promise<string>} 新的access_token
   */
  async doRefreshToken() {
    const refreshToken = storage.get('refreshToken')

    if (!refreshToken) {
      throw new Error('AUTH_EXPIRED')
    }

    try {
      console.log('开始刷新token...')
      // refreshTokenAPI 现在包含 skipAuth: true，不会触发请求拦截器的token检查
      const result = await refreshTokenAPI(refreshToken)
      if (result.access_token && result.refresh_token) {
        // 更新token
        const access_token = result.access_token
        const refresh_token = result.refresh_token
        const access_token_expires_in = result.access_token_expires_in
        storage.set('token', access_token)
        // 如果返回了新的refresh_token（滑动延期），更新它
        if (refresh_token && refresh_token !== refreshToken) {
          storage.set('refreshToken', refresh_token)
          console.log('refresh_token已更新（滑动延期）')
        }
        // 记录过期时间用于调试
        console.log(`新token有效期: ${access_token_expires_in}秒`)

        return access_token
      } else {
        throw new Error('REFRESH_FAILED')
      }
    } catch (error) {
      console.error('Token刷新失败:', error)
      // 清除无效的token
      storage.remove('token')
      storage.remove('refreshToken')
      throw new Error('AUTH_EXPIRED')
    }
  }

  /**
   * 检查认证状态
   * @returns {Promise<string>} 'valid' | 'need_refresh' | 'expired' | 'no_token'
   */
  async checkAuthStatus() {
    const accessToken = storage.get('token')
    const refreshToken = storage.get('refreshToken')

    if (!accessToken && !refreshToken) {
      return 'no_token'
    }

    if (accessToken && !this.isTokenExpired(accessToken)) {
      return 'valid'
    }

    if (refreshToken && !this.isTokenExpired(refreshToken)) {
      return 'need_refresh'
    }

    return 'expired'
  }

  /**
   * 清除所有token
   */
  clearTokens() {
    storage.remove('token')
    storage.remove('refreshToken')
    this.refreshPromise = null
  }

  /**
   * 设置tokens（登录后调用）
   * @param {string} accessToken - access token
   * @param {string} refreshToken - refresh token
   */
  setTokens(accessToken, refreshToken) {
    if (accessToken) {
      storage.set('token', accessToken)
    }
    if (refreshToken) {
      storage.set('refreshToken', refreshToken)
    }
  }

  /**
   * 获取当前存储的tokens
   * @returns {{accessToken: string|null, refreshToken: string|null}}
   */
  getTokens() {
    return {
      accessToken: storage.get('token'),
      refreshToken: storage.get('refreshToken')
    }
  }
}

// 导出单例
export default new TokenManager()
